import { useEffect, useState } from "react";
import Seo from "../components/Seo";
import { getAdminStats, getLeads, markContacted, refreshFeed, API } from "../lib/api";

const TYPES = [
  "all", "vehicle_inquiry", "find_me_vehicle", "build_your_own_deal", "trade_value",
  "payment_help", "ev_help", "vehicle_alert", "contact", "test_drive",
  "saved_vehicles", "compare_vehicles",
];

export default function Admin() {
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [type, setType] = useState("all");
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const s = await getAdminStats();
      setStats(s);
      const l = await getLeads(type === "all" ? undefined : type);
      setLeads(l.items || []);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [type]);

  const onContact = async (id) => {
    await markContacted(id);
    load();
  };

  const onRefreshFeed = async () => {
    await refreshFeed();
    load();
  };

  const filtered = leads.filter((l) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return [l.name, l.email, l.phone, l.vehicle_title, l.lead_source, l.message]
      .filter(Boolean)
      .some((x) => String(x).toLowerCase().includes(s));
  });

  return (
    <main id="main-content" className="bg-[var(--ssc-silver-2)] min-h-screen">
      <Seo title="Admin Dashboard | Scott Sells Chevys" description="Internal admin dashboard." canonicalPath="/admin" />
      <div className="ssc-container py-8">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
          <h1 className="ssc-display text-2xl md:text-3xl font-extrabold text-[var(--ssc-navy)]" data-testid="admin-title">Admin Dashboard</h1>
          <div className="flex gap-2">
            <button type="button" onClick={onRefreshFeed} className="ssc-btn ssc-btn-navy" data-testid="admin-refresh-feed">
              Refresh Inventory Feed
            </button>
            <a href={`${API}/leads/export.csv`} className="ssc-btn ssc-btn-outline" data-testid="admin-export-csv">
              Export Leads CSV
            </a>
          </div>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <Stat label="Total Leads" value={stats.leads.total} />
            <Stat label="New Leads" value={stats.leads.new} />
            <Stat label="Inventory" value={stats.inventory.total} />
            <Stat label="New / Used" value={`${stats.inventory.new} / ${stats.inventory.used}`} />
            <Stat label="Sold/Unavailable" value={stats.inventory.sold} />
            <Stat label="Missing Photos" value={stats.inventory.missing_photos} />
            <Stat label="Last Feed Import" value={stats.feed?.last_import_at ? new Date(stats.feed.last_import_at).toLocaleString() : "—"} />
            <Stat label="Vehicles Imported" value={stats.feed?.vehicles_imported ?? "—"} />
          </div>
        )}

        {/* Lead type counts */}
        {stats?.leads?.by_type && (
          <div className="mb-6">
            <h2 className="text-lg font-extrabold text-[var(--ssc-navy)] mb-2">Leads by type</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
              {Object.entries(stats.leads.by_type).map(([k, v]) => (
                <div key={k} className="ssc-card p-3">
                  <div className="text-xs uppercase text-[var(--ssc-ink-2)]">{k.replace(/_/g, " ")}</div>
                  <div className="text-xl font-extrabold text-[var(--ssc-navy)]">{v}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Alerts */}
        {stats?.alerts && stats.alerts.length > 0 && (
          <div className="ssc-card mb-6 border-l-4 border-[var(--ssc-red)]" data-testid="admin-alerts">
            <div className="font-extrabold text-[var(--ssc-red)]">Alerts</div>
            <ul className="list-disc pl-5 text-[var(--ssc-ink-2)] mt-1">
              {stats.alerts.map((a, i) => <li key={i}>{a}</li>)}
            </ul>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap gap-3 mb-4 items-end">
          <div>
            <label className="ssc-label">Filter by lead type</label>
            <select className="ssc-select" value={type} onChange={(e) => setType(e.target.value)} data-testid="admin-type-filter">
              {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div className="flex-1 min-w-[220px]">
            <label className="ssc-label">Search leads</label>
            <input className="ssc-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Name, email, phone, message..." data-testid="admin-search" />
          </div>
        </div>

        {/* Lead table */}
        <div className="ssc-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left border-b border-[var(--ssc-silver)]">
                {["Created", "Type", "Name", "Phone", "Email", "Vehicle", "Source", "Contacted", "Actions"].map((h) => (
                  <th key={h} className="p-2">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {busy && <tr><td colSpan="9" className="p-4 text-[var(--ssc-ink-2)]">Loading...</td></tr>}
              {!busy && filtered.length === 0 && <tr><td colSpan="9" className="p-4 text-[var(--ssc-ink-2)]">No leads found.</td></tr>}
              {filtered.map((l) => (
                <tr key={l.id} className="border-b border-[var(--ssc-silver)]" data-testid={`lead-row-${l.id}`}>
                  <td className="p-2 whitespace-nowrap">{new Date(l.created_at).toLocaleString()}</td>
                  <td className="p-2 text-[var(--ssc-navy)] font-semibold">{l.lead_type}</td>
                  <td className="p-2">{l.name}</td>
                  <td className="p-2">{l.phone}</td>
                  <td className="p-2">{l.email}</td>
                  <td className="p-2">{l.vehicle_title || "—"}</td>
                  <td className="p-2">{l.lead_source}</td>
                  <td className="p-2">{l.contacted ? "✓" : "—"}</td>
                  <td className="p-2">
                    {!l.contacted && (
                      <button type="button" onClick={() => onContact(l.id)} className="ssc-btn ssc-btn-outline" data-testid={`mark-contacted-${l.id}`}>
                        Mark contacted
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </main>
  );
}

const Stat = ({ label, value }) => (
  <div className="ssc-card">
    <div className="text-xs uppercase tracking-wider text-[var(--ssc-ink-2)]">{label}</div>
    <div className="text-2xl font-extrabold text-[var(--ssc-navy)] mt-1">{value ?? "—"}</div>
  </div>
);
